import React, { useCallback } from "react";
import { Flex, Space, Typography } from "antd";
import { chatApi } from "../../../../services/api";
import { RatingButtons } from "../../../ChatUtils";
import type { ChatMessage } from "../types";
import { createV2MessageTimeFooter } from "./messageProcessor";

interface UseQuickQuestionHandlersProps {
  csUuidRef: React.MutableRefObject<string | undefined>;
  setMessages: React.Dispatch<React.SetStateAction<any[]>>;
  messageRatings: Record<string, number>;
  handleRateMessage: (uuid: string, rating: number) => Promise<void>;
  styles: any;
  uiVariant?: "legacy" | "v2";
  /** 开启机器人跟进时：加速轮询等待 Agent 补充回复 */
  onAwaitReplyStart?: (opts?: { minReplyAfterSec?: number }) => void;
}

/**
 * 功能描述：判断快捷问题是否触发了机器人跟进
 * 业务逻辑：服务端将 USER 置为 waitAnswer 表示需要 Agent 继续回复
 */
const hasBotFollowUp = (memories: any[]): boolean => {
  if (!Array.isArray(memories)) return false;
  return memories.some(
    (item) => item?.type === "USER" && item?.status === "waitAnswer"
  );
};

export const useQuickQuestionHandlers = ({
  csUuidRef,
  setMessages,
  messageRatings,
  handleRateMessage,
  styles,
  uiVariant = "legacy",
  onAwaitReplyStart,
}: UseQuickQuestionHandlersProps) => {
  // 处理快捷问题点击
  const handleQuickQuestionClick = useCallback(
    async (question: Bot.QuickQuestion) => {
      try {
        console.log("快捷问题点击:", question);

        // 调用快捷回复接口
        const response = await chatApi.quickAnswer({
          csUuid: csUuidRef.current,
          quickQaId: question.id,
        });

        if (response.success && response.data) {
          console.log("快捷回复成功，返回数据:", response.data);

          // 将返回的数据转换为消息格式（预设答案为 PROLOGUE，展示为 ai）
          const newMessages = response.data.map((item: any) => {
            const itemTimestamp = item.modifyTime || item.createTime;
            const isUser = item.type === "USER";
            // PROLOGUE 与分类回复一致：不展示评分
            const showRating = item.type === "BOT" || item.type === "HUMAN";
            const message: ChatMessage = {
              key: `${item.uuid}`,
              role: isUser ? "local" : "ai",
              content: item.content,
              typing: false,
              uuid: item.uuid,
              rating: messageRatings[item.uuid],
              timestamp: itemTimestamp,
              footer:
                uiVariant === "v2" && itemTimestamp
                  ? createV2MessageTimeFooter(itemTimestamp)
                  : (
                <Flex
                  justify="space-between"
                  align="center"
                  className={styles.fullWidthRow}
                >
                  <Space size={8}>
                    <Typography.Text
                      type="secondary"
                      className={styles.smallTimestamp}
                    >
                      {new Date(itemTimestamp * 1000).toLocaleString(
                        "zh-CN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography.Text>
                  </Space>
                  {showRating && (
                    <RatingButtons
                      uuid={item.uuid}
                      rating={messageRatings[item.uuid]}
                      onRate={handleRateMessage}
                    />
                  )}
                </Flex>
                  ),
            };
            return message;
          });

          // 添加新消息到聊天记录（避免重复）
          setMessages((prevMessages) => {
            const existingKeys = new Set(prevMessages.map((msg) => msg.key));
            const uniqueNewMessages = newMessages.filter(
              (msg: any) => !existingKeys.has(msg.key)
            );
            return [...prevMessages, ...uniqueNewMessages];
          });

          // 预设为 PROLOGUE，不会误判为 Agent BOT 回复；直接进入等待轮询
          if (hasBotFollowUp(response.data)) {
            onAwaitReplyStart?.();
          }
        } else {
          console.error("快捷回复失败:", response);
        }
      } catch (error) {
        console.error("快捷回复异常:", error);
      }
    },
    [
      csUuidRef,
      messageRatings,
      handleRateMessage,
      styles.fullWidthRow,
      styles.smallTimestamp,
      setMessages,
      uiVariant,
      onAwaitReplyStart,
    ]
  );

  return {
    handleQuickQuestionClick,
  };
};
